import { useRef, useState } from 'react';
import { trpc } from '../lib/trpc';
import { useAsync } from '../lib/useAsync';
import { Loading, ErrorBox, EmptyBox, PageTitle, StatusPill } from '../components/admin/ui';

/**
 * Tela /boletos (incorporadora): fila de pedidos de 2ª via feitos pelos
 * compradores no aplicativo. Anexar o arquivo entrega na conversa do
 * comprador e envia por e-mail com o boleto em anexo.
 */
export function BoletosPage() {
  const { data: pedidos, loading, error, reload } = useAsync(() => trpc.obra.boletos.query());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pedidoAlvo = useRef<string | null>(null);

  function escolherArquivo(pedidoId: string) {
    pedidoAlvo.current = pedidoId;
    inputRef.current?.click();
  }

  async function anexar(file: File) {
    const pedidoId = pedidoAlvo.current;
    if (!pedidoId) return;
    setBusyId(pedidoId);
    setMsg(null);
    try {
      const buf = await file.arrayBuffer();
      const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
      await trpc.obra.atenderBoleto.mutate({ pedidoId, arquivoBase64: b64, arquivoNome: file.name });
      setMsg('Boleto enviado. O comprador recebe na conversa do aplicativo e por e-mail.');
      reload();
    } catch (e) {
      setMsg((e as Error).message || 'Não foi possível anexar o arquivo. Tente de novo.');
    } finally {
      setBusyId(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const pendentes = (pedidos ?? []).filter((p) => p.status === 'pendente');
  const atendidos = (pedidos ?? []).filter((p) => p.status === 'atendido');

  return (
    <div>
      <PageTitle titulo="Boletos" />
      <p className="-mt-4 mb-6 max-w-2xl text-sm text-ink-3">
        Pedidos de segunda via feitos pelos compradores no aplicativo. Anexe o arquivo e ele chega na
        conversa e no e-mail do comprador na hora.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void anexar(f);
        }}
        data-testid="boleto-arquivo-input"
      />
      {loading && <Loading />}
      {error && <ErrorBox msg={error} onRetry={reload} />}
      {msg && (
        <div
          className="mb-4 rounded-xl px-4 py-3 text-sm"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
          data-testid="boleto-msg"
        >
          {msg}
        </div>
      )}
      {pedidos && pedidos.length === 0 && (
        <EmptyBox
          titulo="Nenhum pedido por aqui"
          descricao="Quando um comprador pedir a segunda via no aplicativo, o pedido aparece nesta fila."
        />
      )}

      {pendentes.length > 0 && (
        <section className="mb-8">
          <div className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.1em] text-ink-4">
            Aguardando você
          </div>
          <div className="space-y-2.5">
            {pendentes.map((p) => (
              <div key={p.id} className="card flex flex-wrap items-center gap-4 p-4" data-testid="boleto-pendente">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-ink">
                    {p.compradorNome} · {p.unidade}
                  </div>
                  <div className="mt-0.5 text-xs text-ink-3">
                    Parcela {p.parcelaNumero}
                    {p.parcelaVencimento ? ` · vence ${p.parcelaVencimento}` : ''} · pedido em{' '}
                    {new Date(p.criadoEm as unknown as string).toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <StatusPill label="Pendente" color="var(--amber)" />
                <button
                  type="button"
                  disabled={busyId === p.id}
                  onClick={() => escolherArquivo(p.id)}
                  className="btn btn-primary disabled:opacity-60"
                  data-testid="boleto-anexar"
                >
                  {busyId === p.id ? 'Enviando…' : 'Anexar boleto'}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {atendidos.length > 0 && (
        <section>
          <div className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.1em] text-ink-4">Atendidos</div>
          <div className="space-y-2">
            {atendidos.map((p) => (
              <div key={p.id} className="card flex flex-wrap items-center gap-4 p-4" data-testid="boleto-atendido">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-ink">
                    {p.compradorNome} · {p.unidade} · Parcela {p.parcelaNumero}
                  </div>
                  <div className="mt-0.5 text-xs text-ink-3">
                    {p.arquivoNome ?? 'arquivo'} · atendido em{' '}
                    {p.atendidoEm ? new Date(p.atendidoEm as unknown as string).toLocaleDateString('pt-BR') : ''}
                  </div>
                </div>
                <StatusPill label="Atendido" color="var(--accent)" />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
